import React, { useState, useEffect, useCallback, useMemo, useImperativeHandle, useRef } from "react";
import { useTransition, animated } from "@react-spring/web";
import { closestCenter, DndContext, DragCancelEvent, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { restrictToWindowEdges } from "@dnd-kit/modifiers";

import Button from "src/components/button";
import { Input } from "src/components/input";
import Reveal from "src/components/animation";
import Actions from "src/components/actionbar";
import smartFilters from "./smartFilters";
import { EntityCard } from "../../selectedContentCard/entityCard";
import Slider from "src/components/slider";
import { EdithubContext } from "../index";

import _ from "lodash";
import cx from "classnames";
import styles from "./selectContent.module.scss";
import { useHubAdoption } from "src/redux/actions/adoption/hub";
import { EntityType } from "src/types/adoption/hub";
import { useSelector } from "react-redux";
import userRoleService from "src/utils/userRoles.service";


export type EntityTreeHandler = {
  handleEntityTreeCancel: () => void;
};

type SelectContentPropsType = {
  isTypeSelectionMode: boolean;
  setIsTypeSelectionMode: (value: boolean) => void;
};

const SelectContent = React.forwardRef<EntityTreeHandler, SelectContentPropsType>(({
  isTypeSelectionMode,
  setIsTypeSelectionMode
}, ref) => {

  const { rowItem, updateRowItem,
    activeColumnIndex, setActiveColumnIndex,
    persistedSelectedEntities,
    selectedEntity, setSelectedEntity,
    showEntityTree, setShowEntityTree,
    createSection
  } = React.useContext(EdithubContext);

  const contentType = rowItem[activeColumnIndex]?.section?.entityType;
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const isRecommendedAssetsEnabled = useSelector((state:any) => _.get(state.accounts, "item.recommendationEnabled", false));

  const [showSmartFilters, setShowSmartFilters] = useState<boolean>(false);
  const [showSelectedEntites, setShowSelectedEntites] = useState<boolean>(false);
  const [smartFilter, setSmartFilter] = useState<string>(
    "recently_viewed_streams",
  );
  const [showSlider, setShowSlider] = useState(false);
  const [activeDragEntityId, setActiveDragEntityId] = useState<string | number>();


  const isExistingLayout = useMemo(() => {
    return _.get(rowItem[activeColumnIndex], "section.id") ? true : false;
  }, [rowItem])

  const isUpdatingLayout = useMemo(() => {
    return _.get(rowItem[activeColumnIndex], "section.id") ? true : false;
  }, [rowItem])

  const { trackSmartFilterConfirmationScreenView, trackHubSectionContentSelectionTypeConfirmEvent, trackHubSectionContentSelectClicked,
    trackHubSectionContentSelectionTypeSelectedEvent, trackHubSectionManageContentClicked, trackSectionContentRemovedEvent } = useHubAdoption();

  useEffect(() => {
    if (!showSmartFilters && showSlider) {
      trackSmartFilterConfirmationScreenView();
    }
  }, [showSlider, showSmartFilters])

  useEffect(() => {
    const smartFilterValue = rowItem[activeColumnIndex]?.section?.smartFilter;
    setSmartFilter((smartFilterValue && smartFilterValue !== "unspecified") ? smartFilterValue : "recently_viewed_streams");
  }, [rowItem, activeColumnIndex])

  useEffect(() => {
    if (isUpdatingLayout) {
      const isSmartFilter = _.get(rowItem[activeColumnIndex], 'section.smartFilter') && _.get(rowItem[activeColumnIndex], 'section.smartFilter') != "unspecified" ? true : false;
      
      setIsTypeSelectionMode(false);
      if (isSmartFilter) {
        setShowSmartFilters(true);
        setShowSelectedEntites(false);
      }
      else {
        setShowSelectedEntites(true);
        setShowSmartFilters(false);
      }
    }
    else {
      setIsTypeSelectionMode(true);
      setShowSelectedEntites(false);
      setShowSmartFilters(false);
    }
  }, [contentType])

  const handleEntityTreeCancel = () => {

    setShowEntityTree(false);
    const isUpdatingLayout = _.get(rowItem[activeColumnIndex], "section.id") ? true : false;
    if (!createSection && isUpdatingLayout && _.size(persistedSelectedEntities) > 0) {
      const updatedSelectedEntity = {
        ...selectedEntity,
        [activeColumnIndex]: persistedSelectedEntities[activeColumnIndex]
      };
      setSelectedEntity(updatedSelectedEntity);
    }
    else {
      const updatedSelectedEntity = {
        ...selectedEntity,
        [activeColumnIndex]: []
      };
      setSelectedEntity(updatedSelectedEntity);
    }
    setIsTypeSelectionMode(!isExistingLayout);
    setShowSelectedEntites(isExistingLayout);
  }

  useImperativeHandle(ref, () => ({
    handleEntityTreeCancel,
  }));

  const handleInputChange = (value: string) => {
    updateRowItem("title", value)
  };

  const handleSliderConfirm = () => {
    setShowSlider(false);
    trackHubSectionContentSelectionTypeConfirmEvent(showSmartFilters ? "Manual" : "Smart Filter");
    if (showSmartFilters) {
      updateRowItem("smartFilter", null);
      setShowEntityTree(true)
      setShowSmartFilters(false)
      setShowSelectedEntites(true);
    } else {
      setShowSmartFilters(true)
      setShowEntityTree(false);
      setShowSelectedEntites(false);
      const firstFilter = smartFilters.smartFilters
        .filter((filter) => filter.contentType === contentType);
      updateRowItem("smartFilter", firstFilter[0]?.type);
    }
  }

  const transitions = useTransition(showSmartFilters, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 0 },
  });

  const filtersSelectionTransition = useTransition(isTypeSelectionMode, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 0 },
    config: { duration: 250 }
  });


  const sensors = useSensors(
    useSensor(PointerSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragEntityId(_.get(event, "active.id"))
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragEntityId("")

    const { active, over } = event;
    const items = selectedEntity[activeColumnIndex];
    if (active && over && _.get(active, "id", "") !== _.get(over, "id", "")) {
      const oldIndex = _.findIndex(items as any, (item) => _.get(item, 'id') === active.id);
      const newIndex = _.findIndex(items as any, (item) => _.get(item, 'id') === over.id);
      const updatedArray = arrayMove(items as any, oldIndex, newIndex);

      const updatedSelectedEntity = {
        ...selectedEntity,
        [activeColumnIndex]: updatedArray,
      }
      setSelectedEntity(updatedSelectedEntity);

    }
  };

  const handleDragCancel = (event: DragCancelEvent) => {
    setActiveDragEntityId("");
  }

  const getActiveDragEntity = useMemo(() => {

    const entities = selectedEntity[activeColumnIndex] as any;
    const activeDragEntity = _.find(entities, (item) => _.get(item, "id", "") === activeDragEntityId)

    return { activeDragEntity };
    
  }, [activeDragEntityId, selectedEntity, activeColumnIndex])

  const { activeDragEntity } = getActiveDragEntity;

  const getItems = useMemo(() => {
    if(_.size(selectedEntity[activeColumnIndex]) > 0){
      return selectedEntity[activeColumnIndex]?.map(entity => _.get(entity, "id",""))
    }
    return [];
  },[selectedEntity])

  const renderSelectContent = useCallback(
    () => (
      <div className={styles.pickFilter}>

        {!showEntityTree && <div className={cx(styles.switchContent, {
          [styles.showAddAssetIcon]: !showEntityTree && showSelectedEntites,
        })}>
          <Reveal show={true}>
            <div className={styles.messageSection}>
              <p className={styles.messageText}>
                Switch to <br />
                <b>{showSmartFilters ? "Manual Selection" : "Smart Filters"}</b>
              </p>
              <Actions.IconButton
                icon="paperflite-reverse"
                onClick={() =>
                  setShowSlider(true)
                }
              />

              <Actions.IconButton
                className={styles.addIcon}
                icon="paperflite-add"
                tooltip="Add Content"
                onClick={() => {
                  trackHubSectionManageContentClicked();
                  setShowEntityTree(true)}
                }
              />
            </div>
          </Reveal>
        </div>}

        {showSelectedEntites &&

          <div className={styles.selectedContent}>
            {_.size(selectedEntity[activeColumnIndex]) === 0 && _.times(3, (index) => (
              <div key={index} className={styles.placeholderCard} />
            ))}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              onDragCancel={handleDragCancel}
            > 
              <SortableContext items={getItems} strategy={rectSortingStrategy}>
                  {_.size(selectedEntity[activeColumnIndex]) > 0 && _.size(selectedEntity[activeColumnIndex]) > 0 &&
                    selectedEntity[activeColumnIndex].map((entity, index) => {
                      return <EntityCard
                        key={entity?.id}
                        entity={entity}
                        activeDragEntityId={activeDragEntityId}
                        onRemove={() => {
                          const prevItems = selectedEntity[activeColumnIndex];
                          const updatedItems = _.reject(prevItems, { id: entity.id });
                          const updatedSelectedEntity = {
                            ...selectedEntity,
                            [activeColumnIndex]: updatedItems,
                          }
                          setSelectedEntity(updatedSelectedEntity);
                          trackSectionContentRemovedEvent(entity.entityType as EntityType)
                        }}
                      />
                    })}
              </SortableContext>
              <DragOverlay modifiers={[restrictToWindowEdges]}>
                {activeDragEntity && activeDragEntityId && <EntityCard entity={activeDragEntity} />}
              </DragOverlay>

            </DndContext>
            <Reveal show={_.size(selectedEntity[activeColumnIndex]) === 0 && !showEntityTree}>

              <div className={cx(styles.filterOption, styles.manualSelectionPlaceholder)}>
                <img
                  src="/images/hub/editLayout/selectContent.svg"
                  alt="Manual Selection"
                />
                <h2>Manual Selection</h2>
                <p>
                  Choose each content piece to be displayed in the section by yourself. Ideal when you want full control (hello, control freaks).
                </p>
                <Button
                  className={styles.filterButton}
                  icon="paperflite-add"
                  text="Add Content"
                  variant="primary"
                  onClick={() => {
                    setShowEntityTree(true);
                    setShowSmartFilters(false);
                    setIsTypeSelectionMode(false);
                    setShowSelectedEntites(true);
                    updateRowItem("smartFilter", null);
                    trackHubSectionContentSelectClicked();
                  }}
                />
              </div>
            </Reveal>

          </div>

        }

        {showSmartFilters && <div className={styles.smartFilterContainer}>
          <p className={styles.pickFilterHeading}>Pick a smart filter</p>
          <p className={styles.pickFilterSubHeading}>
          Let our smart filters do the heavy lifting. Pick a filter, grab a coffee.
          </p>

          {transitions(
            (style, item) =>
              item && (
                <animated.div style={style} className={styles.filters}>
                  {smartFilters.smartFilters
                    .filter((filter) => filter.contentType === contentType)
                    .map((filter) => {
                      if((!isRecommendedAssetsEnabled && filter.type === "recommended_assets") || (!userRoleService.canShare() && filter.type=== "frequently_shared_assets")){
                        return <></>
                      }
                    return (
                      <div
                      key={filter.name}
                      className={cx(
                        styles.filterItem,
                        filter.type === smartFilter &&
                        styles.activeFilterItem,
                      )}
                      onClick={() => {
                        updateRowItem("smartFilter", filter.type);
                      }}
                    >
                      <i
                        className={cx(
                          filter.type === smartFilter
                            ? "fa-solid fa-check-circle"
                            : "paperflite-radio-button-unselected",
                          styles.selectIcon
                        )}
                        aria-hidden="true"
                      ></i>
                      <img
                        src={filter.icon}
                        alt="Smart Filters"
                        className={styles.filterImage}
                      />
                      <h3 className={styles.filterName}>{filter.name}</h3>
                      <p className={styles.filterDescription}>
                        {filter.description}
                      </p>
                    </div>
                    )
                    }
                    )}
                </animated.div>
              ),
          )}
        </div>}


        <Slider.Dialog show={showSlider} width="400" onClose={() => setShowSlider(false)}>
          <Slider.Header
            title={`Switch to: “${showSmartFilters ? "manual selection" : "smart Filters"}”`}
          >
          </Slider.Header>

          <Slider.Body className={styles.sliderBody}>
            <img
              className={styles.sliderImage}
              src={`/images/hub/sliders/${showSmartFilters ? "manualSelection" : "smartFilter"}.svg`}
              alt="Smart Filters"
            />
            {showSmartFilters ? "Ready to go full content curator? You'll handpick assets without our smart filter training wheels."
              : "Let our smart filters play your content matchmaker. Sure you want to continue?"}
          </Slider.Body>

          <Slider.Actions>
            <Button variant="primary"
              icon="paperflite-tick"
              text={'Confirm'}
              disabled={false}
              onClick={handleSliderConfirm}
            />
            <Button text="Cancel" disabled={false} onClick={() => setShowSlider(false)} />
          </Slider.Actions>
        </Slider.Dialog>

      </div >
    ),
    [smartFilter, contentType, transitions, activeColumnIndex],
  );

  const handleSwitchSection = (index) => {
    if (index === activeColumnIndex || showEntityTree) return;

    setActiveColumnIndex(index);
    const isSectionUpdated = rowItem[index]?.section?.tempId ? true : false;
    if (isSectionUpdated || isExistingLayout) {
      const isSmartFilter =  _.get(rowItem[index], "section.smartFilter") && _.get(rowItem[index], "section.smartFilter") !== "unspecified"  ? true : false;
      if (isSmartFilter) {
        setShowSmartFilters(true);
        setShowSelectedEntites(false);
      }
      else {
        setShowSelectedEntites(true);
        setShowSmartFilters(false);
        setShowEntityTree(true);
      }
      setIsTypeSelectionMode(false);
    }
    else {
      setShowSelectedEntites(false);
      setShowSmartFilters(false);
      setIsTypeSelectionMode(true);
    }
  }

  return (
    <animated.div>
      <div className={styles.smartFilters}>

        <div className={cx(styles.columnInputs)}>
          {rowItem.map((column, index) => (
            <div key={index} className={cx(styles.columnInput, {
              [styles.showDivider]: _.size(rowItem) > 1
            })} onClick={() => handleSwitchSection(index)}>
              <input className={cx(styles.inputField, {
                [styles.activeInput]: index === activeColumnIndex,
                [styles.disabledInput]: showEntityTree && index !== activeColumnIndex
              })}
                placeholder="Add a section name"
                value={column.section.title}
                onChange={(e) => {
                  handleInputChange(e.target.value)
                }} />
            </div>
          ))}
        </div>
        {filtersSelectionTransition(
          (style, item) =>
            item && (<animated.div style={style} className={styles.filtersContainer}>
              <div className={styles.filterOption}>
                <img
                  src="/images/hub/editLayout/smartFilters.svg"
                  alt="Smart Filters"
                />
                <h2>Smart Filters</h2>
                <p>
                Leave the content selection to our smart content filters. Showcase dynamic content based on the smart filter picked.
                </p>
                <Button
                  className={styles.filterButton}
                  icon="paperflite-filter"
                  text="Choose filter"
                  variant="primary"
                  onClick={() => {
                    setShowSmartFilters(true);
                    setShowEntityTree(false);
                    setIsTypeSelectionMode(false);
                    setShowSelectedEntites(false);
                    const firstFilter = smartFilters.smartFilters
                      .filter((filter) => filter.contentType === contentType);
                    updateRowItem("smartFilter", firstFilter[0]?.type);
                    trackHubSectionContentSelectionTypeSelectedEvent("Smart Filter");
                  }}
                />
              </div>

              <div className={styles.divider}>or</div>

              <div className={styles.filterOption}>
                <img
                  src="/images/hub/editLayout/selectContent.svg"
                  alt="Manual Selection"
                />
                <h2>Manual Selection</h2>
                <p>
                  Choose each content piece manually. For when you want total control.
                </p>
                <Button
                  className={styles.filterButton}
                  icon="paperflite-add"
                  text="Add Content"
                  variant="primary"
                  onClick={() => {
                    setShowEntityTree(true);
                    setShowSmartFilters(false);
                    setIsTypeSelectionMode(false);
                    setShowSelectedEntites(true);
                    updateRowItem("smartFilter", null);
                    trackHubSectionContentSelectClicked();
                    trackHubSectionContentSelectionTypeSelectedEvent("Manual");
                  }}
                />
              </div>
            </animated.div>))}

        {!isTypeSelectionMode && renderSelectContent()}
      </div>
    </animated.div>
  );
});

export default SelectContent;
